/**
 * Plain-English help for every control on the algorithm page.
 *
 * Written for someone who has never seen the code: what the setting is, what
 * changes if they move it up or down, and a concrete example. Each entry is a
 * list of small sections so the dialog can lay them out consistently.
 */

const section = (label, text) => ({ label, text });

/** The whole algorithm, explained once. Opened from the heading. */
export const OVERVIEW_HELP = {
  title: "How the feed decides what people see",
  sections: [
    section(
      "The short version",
      "Every reel and post is given a number of points. The more points something has, the earlier people see it. This page is where you decide how those points are worked out.",
    ),
    section(
      "Step 1 - add up what happened to it",
      "We count the good things: how many people watched it, liked it, commented on it, shared it, and the star points and popularity it earned. You decide how much each of those is worth, using the weights further down this page. Most apps make a share worth more than a like, because sharing takes real effort while a like takes a second.",
    ),
    section(
      "Step 2 - take points away as it gets older",
      "A reel posted an hour ago and a reel posted last year should not compete on equal terms. So points fade as content ages. The 'Freshness decay' setting controls how fast that happens - it is the single biggest lever on how the feed feels.",
    ),
    section(
      "Big numbers cannot bully small ones",
      "Views are counted in thousands; shares in tens. If we simply added them up, views would decide everything and your other settings would do nothing at all. So each number is flattened before it is weighed: going from 10 views to 100 counts roughly the same as going from 100 to 1,000. This is why a small weight on shares can still matter more than a big weight on views.",
    ),
    section(
      "Nobody sees the same thing twice",
      "The app remembers what it has already shown each person. That memory survives closing and reopening the app, which is what stops the same reels coming round again. You control how long that memory lasts.",
    ),
    section(
      "Room is kept for new content",
      "A brand-new post starts with zero points, so if we only ever showed the highest scorers, nothing new could ever get seen - and it would never earn the engagement it needs to climb. A slice of every page is therefore set aside for content few people have seen yet. This is how new creators get a chance.",
    ),
    section(
      "If something goes wrong",
      "Turn off 'Use this algorithm' at the top. The feed goes straight back to how it worked before, immediately, with no help from a developer. Nothing is lost - your settings are still saved for when you switch it back on.",
    ),
  ],
};

/** Help for the switches at the top of the page. */
export const TOGGLE_HELP = {
  enabled: {
    title: "Use this algorithm",
    sections: [
      section("What this is", "The master switch. On means the feed is ordered by the points on this page. Off means the feed goes back to exactly how it behaved before this page existed."),
      section("When you would turn it off", "If the feed starts showing something odd and you need it back to normal right now. It takes effect immediately and does not need anybody to deploy anything."),
      section("Good to know", "Turning it off does not erase your settings. Everything you have set here is kept, and switching back on restores it."),
    ],
  },
  antiRepeatEnabled: {
    title: "Do not repeat content",
    sections: [
      section("What this is", "The app remembers what it has already shown each person, so the same reel or post does not keep coming back."),
      section("Why it matters", "Without this, every request is a fresh roll of the dice and people see the same content again and again - including every time they close and reopen the app. This is the setting that fixes that."),
      section("If you turn it off", "People will start seeing repeats. There is very little reason to switch this off except to test something."),
    ],
  },
  pinnedEnabled: {
    title: "Use pinned positions",
    sections: [
      section("What this is", "Whether the positions you set with 'select top' are used. When it is on, a reel or post you pin to position 3 appears in position 3, and the rest of the feed fills in around it."),
      section("If you turn it off", "Pinning is ignored and everything competes on points alone. Your pins are not deleted - they stay saved, so switching this back on restores every position exactly as you set it."),
      section("When you would turn it off", "To see what the feed looks like on its own merit, or to quickly stand down all promoted content without unpinning it item by item."),
      section("Worth knowing", "Pinned content keeps its position even for someone who has already seen it. That is deliberate - if you pinned it, you want it seen. Position numbers follow the page size, so at a page size of 7, positions 1-7 are the first page and 8-14 the second."),
    ],
  },
  locationEnabled: {
    title: "Show nearby content first",
    sections: [
      section("What this is", "Lifts reels and posts from people near the viewer to the top of their feed. Distance is measured from the viewer's saved location to the creator's."),
      section("If you turn it on", "People mostly see content from their own area first. Within that local content, the usual points still decide the order, so the best nearby content comes first."),
      section("If you turn it off", "Location is ignored completely and the feed is the same for everyone, ordered on points alone."),
      section("Important", "It only works for people whose location the app has saved. Anyone without a saved location simply gets the normal feed, so nothing breaks - they just do not get the nearby ordering."),
      section("Worth knowing", "Distant content is never removed, only ranked lower. If there is little nearby content the feed still fills up normally instead of going half empty. Pinned positions always stay above nearby content."),
    ],
  },
  revivalEnabled: {
    title: "Let older content come back",
    sections: [
      section("What this is", "Normally the freshness setting measures from the day something was posted, so an old reel keeps sliding down no matter what happens to it. With this on, anything a real person has engaged with recently - watched, liked, commented, shared, or sent star points to - is measured from that moment instead."),
      section("Why you want it", "It is what gives older content a real second chance. Send star points to a year-old reel and it can climb again, rather than staying buried because of its age."),
      section("Only real engagement counts", "Approving, hiding, pinning or editing something does NOT make it come back - only a genuine interaction from a user does. So switching this on does not suddenly fill the feed with old material; nothing moves until people actually engage with it."),
      section("If you turn it off", "Age alone decides, and old content can never rise again however much attention it gets."),
      section("Worth knowing", "Anything nobody has engaged with still fades exactly as before. And because this tracks engagement from the moment the setting is available, older content stays where it is until someone interacts with it."),
    ],
  },
  oldContentEnabled: {
    title: "Always show some older content",
    sections: [
      section("What this is", "Keeps a fixed share of every page for older reels and posts, so your back catalogue stays visible instead of only the newest material being seen."),
      section("How it differs from 'Let older content come back'", "That setting only lifts old content somebody has already engaged with, so nothing surfaces until people interact with it. This one reserves slots for older content regardless, which is what makes it visible in the first place. They work well together: this gives old content a chance to be seen, and the comeback setting lets anything that earns attention climb properly."),
      section("If you turn it off", "Only the freshness rules apply, and older content appears solely on merit - which in practice means rarely."),
      section("Worth knowing", "Nothing else changes. The reserved slots are still filled by the best-scoring older content, and the rest of the page works exactly as before."),
    ],
  },
  diversityEnabled: {
    title: "Mix up creators",
    sections: [
      section("What this is", "Limits how many reels or posts from the same person can appear on a single page."),
      section("The problem it solves", "When somebody uploads six or seven things and you approve them all at once, they end up with almost identical scores and land next to each other - so the feed reads as one person in a row. This spreads them out over several pages instead."),
      section("If you turn it off", "Whatever scores highest wins, even if that means one creator takes the whole page."),
      section("Worth knowing", "Nothing is thrown away. Items held back are simply shown on a later page, and if there is not enough other content to fill a page the limit is relaxed rather than returning a short page."),
    ],
  },
  rankFollowingFeed: {
    title: "Also rank the following feed",
    sections: [
      section("What this is", "Whether the 'following' feed - the one showing only people you follow - is also ordered by points."),
      section("If you turn it on", "The most popular content from the people someone follows comes first, even if it is a few days old."),
      section("If you turn it off", "The following feed stays close to newest-first. People often expect this: if a friend posts something, they want to see it near the top, not buried under an older popular post."),
      section("Good to know", "Either way, people still never see the same thing twice."),
    ],
  },
};

/** Help for each thing the points are built from. */
export const SIGNAL_HELP = {
  view: {
    title: "Views",
    sections: [
      section("What this is", "How many people have watched this piece of content."),
      section("If you increase it", "Content that lots of people have already watched is pushed further up. The feed starts to feel like a 'most watched' list."),
      section("If you decrease it", "Being watched a lot counts for less, and things like comments and shares decide the order instead."),
      section("Worth knowing", "Views are the easiest number to rack up - a view happens whether or not the person enjoyed it. That is why it is usually given the smallest weight of all."),
    ],
  },
  like: {
    title: "Likes",
    sections: [
      section("What this is", "How many people tapped like."),
      section("If you increase it", "Well-liked content rises. This is a reasonable middle ground - a like means someone actually enjoyed it, not just that it appeared on their screen."),
      section("If you decrease it", "Likes matter less. Useful if you feel people like things out of habit rather than genuine interest."),
      section("Worth knowing", "A like costs almost no effort, so it usually sits above views but below comments and shares."),
    ],
  },
  comment: {
    title: "Comments",
    sections: [
      section("What this is", "How many comments and replies the content has."),
      section("If you increase it", "Content people are talking about rises. This tends to favour posts that spark conversation or debate."),
      section("If you decrease it", "Discussion counts for less and the feed leans towards content people quietly watch."),
      section("Worth knowing", "Writing a comment takes real effort, so it is a strong sign someone cared. Be aware it can also favour arguments, not just good content."),
    ],
  },
  share: {
    title: "Shares",
    sections: [
      section("What this is", "How many times someone sent this content to another person."),
      section("If you increase it", "Content people pass on to friends rises to the top. This is usually the best guide to genuine quality."),
      section("If you decrease it", "Sharing counts for less."),
      section("Worth knowing", "Sharing is the biggest thing you can ask of someone - they are putting their own name behind it. This is why it normally carries the highest weight of all."),
    ],
  },
  starPoint: {
    title: "Star points earned",
    sections: [
      section("What this is", "The star points this particular reel or post has earned - not the creator's total."),
      section("If you increase it", "Content that has earned well is pushed up."),
      section("If you decrease it", "Earnings stop influencing where content appears."),
      section("Worth knowing", "This partly overlaps with views, because content earns as it gets watched. Setting both very high counts the same thing twice."),
    ],
  },
  popularity: {
    title: "Popularity earned",
    sections: [
      section("What this is", "The popularity this particular reel or post has earned."),
      section("If you increase it", "Content that has built popularity is pushed up."),
      section("If you decrease it", "Popularity stops influencing the order."),
      section("Worth knowing", "Like star points, this grows alongside views, so a very high weight here plus a very high weight on views rewards the same thing twice."),
    ],
  },
  creatorPopularity: {
    title: "Creator popularity",
    sections: [
      section("What this is", "The creator's lifetime popularity - about the person, not this particular post."),
      section("If you increase it", "Anything from a well-known creator gets a head start, even a weak post. The feed starts to feel like a list of familiar faces."),
      section("If you decrease it", "Each post rises or falls on its own merit, whoever made it."),
      section("Worth knowing", "Handle this one carefully. Set it high and new creators struggle to be seen at all, because they cannot compete with an established account's history. Keep it low unless you specifically want to favour big accounts."),
    ],
  },
  creatorStarPoint: {
    title: "Creator star points",
    sections: [
      section("What this is", "The creator's lifetime star points - again about the person, not this post."),
      section("If you increase it", "Creators who have earned a lot get a head start on everything they post."),
      section("If you decrease it", "A creator's history stops affecting where their new content appears."),
      section("Worth knowing", "This has the same risk as creator popularity: it rewards people for who they already are rather than what they just made. It is switched off by default."),
    ],
  },
};

/** Help for the tuning numbers. */
export const NUMBER_HELP = {
  gravity: {
    title: "Freshness decay",
    sections: [
      section("What this is", "How quickly content loses its points as it gets older. This is the setting that most changes how the whole feed feels."),
      section("If you increase it", "Old content disappears fast and the feed feels live and current. Set it too high and genuinely good content vanishes before most people ever see it."),
      section("If you decrease it", "Good content stays near the top for longer. Set it to 0 and age is ignored completely - the feed becomes an all-time greatest hits list, and one viral reel from two years ago can sit at the top forever."),
      section("A rough guide", "0 ignores age entirely. 0.6 is a slow burn where good content lasts days. 1.2 is balanced and a sensible starting point. 1.8 means yesterday is already buried."),
    ],
  },
  pinBoost: {
    title: "Pinned bonus",
    sections: [
      section("What this is", "Extra points given to content you have pinned using 'select top'. It is a flat bonus added on top of everything else."),
      section("If you increase it", "Pinned content is more certain to sit at the very top, whatever else is happening."),
      section("If you decrease it", "Pinned content competes more evenly with everything else, and a genuinely popular reel could outrank it."),
      section("Worth knowing", "The default is deliberately very large, so that anything you pin by hand reliably wins. Lower it only if you want pinning to be a nudge rather than a guarantee."),
    ],
  },
  explorationPercent: {
    title: "New content share",
    sections: [
      section("What this is", "The percentage of every page kept aside for content that few people have seen yet, instead of the highest scorers."),
      section("If you increase it", "New creators get seen much more often, and the feed feels fresher and more varied. Push it too high and the average quality drops, because you are showing more unproven content."),
      section("If you decrease it", "People see more of the proven hits. Set it to 0 and you create a trap: new content starts at zero points, never gets shown, so never earns engagement, so never climbs. Nothing new can ever break through."),
      section("Suggested", "Around 20% works well. Do not set it to 0 unless you have a specific reason - it slowly starves the feed of new content."),
    ],
  },
  pageSize: {
    title: "Items per page",
    sections: [
      section("What this is", "How many reels or posts the app receives each time it asks for more."),
      section("If you increase it", "The app fetches in bigger chunks and asks less often. Very large values make each request slower."),
      section("If you decrease it", "Smaller, faster requests, but the app has to ask more often as people scroll."),
      section("Worth knowing", "10 matches what the app was already using. There is rarely a good reason to change it."),
    ],
  },
  seenWindowDays: {
    title: "Repeat after (days)",
    sections: [
      section("What this is", "How long the app remembers that someone actually watched something, before it is allowed to appear for them again."),
      section("If you increase it", "People go much longer without seeing anything twice. But a very keen user can eventually work through everything available, and then has little left to be shown."),
      section("If you decrease it", "There is always plenty to show, but people start recognising content sooner."),
      section("Worth knowing", "If someone does run out, they are shown whatever they saw longest ago rather than an empty feed, so the app never looks broken."),
    ],
  },
  servedWindowHours: {
    title: "Suppress shown for (hours)",
    sections: [
      section("What this is", "How long something is held back after merely appearing on someone's screen, even if they scrolled straight past without watching."),
      section("If you increase it", "Stronger protection against seeing the same thing twice in one sitting or after a quick restart."),
      section("If you decrease it", "Content someone skipped past can come back sooner."),
      section("Worth knowing", "This is a shorter, lighter memory than the one above. Something scrolled past is not really 'seen', so it is held back for hours rather than weeks."),
    ],
  },
  locationRadiusKm: {
    title: "Nearby radius",
    sections: [
      section("What this is", "How far away content can be and still count as nearby, in kilometres."),
      section("If you increase it", "A wider area counts as local - more content qualifies, but 'nearby' means less. Good for places where your users are spread out."),
      section("If you decrease it", "Only genuinely close content is lifted. Sharper local feel, but in a quiet area very little may qualify and most people will just see the normal feed."),
      section("Suggested", "50 km is a sensible start - roughly a city and its surroundings. Try 25 for dense cities, 100+ for rural coverage."),
    ],
  },
  locationPercent: {
    title: "Nearby share of page",
    sections: [
      section("What this is", "How much of each page is given to nearby content. At 50, half of what someone sees is from their own area and the other half is the normal feed."),
      section("If you increase it", "More of the page is local. At 100 the whole page is nearby content whenever there is enough of it, which is how it behaved before this setting existed."),
      section("If you decrease it", "Local content becomes a smaller part of the mix. At 0 it is effectively switched off and the feed is the same for everyone."),
      section("Worth knowing", "The nearby items are spread through the page rather than stacked at the top, so the feed reads as a mix instead of a local block followed by everything else."),
      section("If there is not enough nearby content", "The rest of the page is filled normally, so someone in a quiet area still gets a full feed rather than a short one. Asking for 70% when only two nearby items exist simply gives those two."),
      section("Suggested", "50 is a good starting point. Raise it in dense cities where there is plenty of local content, lower it where users are spread thin."),
    ],
  },
  locationBoost: {
    title: "Nearby priority",
    sections: [
      section("What this is", "Decides the order of the nearby items among themselves. How many of them appear is set by 'Nearby share of page'; this decides which of them come first."),
      section("If you increase it", "Distance matters more than points within the local content, so the very closest items lead."),
      section("If you decrease it", "Points matter more, so the best local content leads even if something else is slightly closer."),
      section("Worth knowing", "This only affects ordering inside the nearby share of the page. It cannot make nearby content take more of the page than the share you set."),
    ],
  },
  revivalWindowDays: {
    title: "Comeback window",
    sections: [
      section("What this is", "How recently something must have been watched, liked or tipped for it to be treated as fresh again."),
      section("If you increase it", "More old content stays eligible for a comeback, so the feed carries more older material overall."),
      section("If you decrease it", "Only content people are engaging with right now comes back, keeping the feed tighter and more current."),
      section("Suggested", "7 days works well. It gives something a genuine week in the sun after it gets attention, then lets it settle back down."),
    ],
  },
  oldContentPercent: {
    title: "Older content share of page",
    sections: [
      section("What this is", "How much of each page is given to older content. At 20, two items in every ten are from the back catalogue."),
      section("If you increase it", "More of the feed is older material. Useful if you have a large library and little new content coming in."),
      section("If you decrease it", "The feed leans towards new content. At 0 it is effectively switched off."),
      section("Worth knowing", "The older items are spread through the page rather than grouped together, and if there is not enough older content the rest of the page fills normally - the page is never left short."),
      section("Suggested", "20 is a sensible start: enough that the back catalogue gets seen, not so much that the feed feels stale."),
    ],
  },
  oldContentAfterDays: {
    title: "Counts as older after",
    sections: [
      section("What this is", "How many days old something must be before it counts towards the older-content share."),
      section("If you increase it", "Only genuinely old material qualifies, so the reserved slots go to your deeper back catalogue."),
      section("If you decrease it", "Content counts as old sooner, so the reserved slots fill with fairly recent material and the setting has less visible effect."),
      section("Suggested", "30 days works well - long enough that something has had its run in the feed, short enough that there is plenty to choose from."),
    ],
  },
  maxPerCreatorPerPage: {
    title: "Max per creator, per page",
    sections: [
      section("What this is", "How many items from the same person are allowed on one page."),
      section("If you increase it", "A popular creator can take more of each page. Set it as high as the page size and the limit effectively stops applying."),
      section("If you decrease it", "A more varied feed with more different people on every page. Set it to 1 and nobody ever appears twice on a page."),
      section("Suggested", "2 works well - a creator can still show up more than once, but never dominates."),
      section("Worth knowing", "Pinned items count towards a creator's share, so pinning two of somebody's reels uses up their allowance on that page."),
    ],
  },
  refreshMinutes: {
    title: "Rescore every (minutes)",
    sections: [
      section("What this is", "How often all the points are worked out again using the latest likes, views, comments and shares."),
      section("If you increase it", "Less work for the server, but content that is taking off right now takes longer to climb."),
      section("If you decrease it", "The feed reacts faster to what is popular at this moment, at the cost of more server work."),
      section("Worth knowing", "Points are always recalculated straight away when you press Save, so you never have to wait to see the effect of a change you just made."),
    ],
  },
};

export default { OVERVIEW_HELP, TOGGLE_HELP, SIGNAL_HELP, NUMBER_HELP };
